import "../shared/IntakeForm.scss";

import Button from "components/Button/Button";
import { ControlledInput } from "components/Card/ControlledInput";
import KaboodleToaster from "components/common/KaboodleToaster";
import { TextInput } from "components/inputs";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import type { ClientIntakeRequest } from "services/intake-api";
import { useRegisterClientIntakeMutation } from "services/intake-api";
import { useIntakeToken, useToken } from "utils/context";

import IntakeHeader from "../components/HeaderIntake";
import {
  useOrganizationSlug,
  formatOrganizationSlug,
} from "../hooks/useOrganizationSlug";
import { logToSentry, getSessionId } from "utils/sentry";

const RegisterIntake = () => {
  const [success, setSuccess] = useState(false);
  const { accessToken } = useToken();
  const { intakeToken } = useIntakeToken();
  const organizationSlug = useOrganizationSlug();
  const uiSessionId = getSessionId();
  const {
    control,
    handleSubmit: handleFormSubmit,
    formState: { isSubmitting },
  } = useForm<ClientIntakeRequest>({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      password_confirm: "",
      phone_number: "",
      organization_slug: organizationSlug || undefined,
    },
  });

  useEffect(() => {
    logToSentry(`Intake registration page`, "info", { uiSessionId });
  }, [uiSessionId]);

  const [registerClientIntake, { isLoading }] =
    useRegisterClientIntakeMutation();

  // for auth pages, just check if tokens exist and redirect
  // the dashboard will handle token validation
  if ((accessToken || intakeToken) && organizationSlug) {
    return <Navigate to={`/intake/${organizationSlug}/dashboard`} replace />;
  }

  const handleFieldChange = async () => Promise.resolve();

  const onSubmit = async (data: ClientIntakeRequest) => {
    const uiSessionId = getSessionId();

    // ensure organization_slug is available
    if (!organizationSlug) {
      KaboodleToaster.error("Organization not found. Please check the URL.");
      logToSentry(
        `Intake registration failed - no organization slug`,
        "error",
        {
          uiSessionId,
          registerEmail: data.email,
        },
      );
      return;
    }

    try {
      // ensure organization_slug is set from URL
      const registrationData: ClientIntakeRequest = {
        ...data,
        organization_slug: organizationSlug,
      };
      await registerClientIntake(registrationData).unwrap();
      logToSentry(`Intake registration success`, "info", {
        uiSessionId,
        registerEmail: data.email,
      });
      setSuccess(true);
    } catch (err: unknown) {
      // handle errors from backend
      if (err && typeof err === "object" && "data" in err) {
        const errorData = err as { data?: { error?: string | string[] } };
        const errorMessage = errorData.data?.error;

        if (errorMessage) {
          // show first error message if it's an array (e.g., password validation errors)
          const message = Array.isArray(errorMessage)
            ? errorMessage[0]
            : errorMessage;
          KaboodleToaster.error(message);
        } else {
          KaboodleToaster.error(err);
        }
      } else {
        KaboodleToaster.error("Registration failed. Please try again.");
      }
      logToSentry(`Intake registration failed`, "error", {
        uiSessionId,
        registerEmail: data.email,
        organizationSlug,
      });
      console.error(err);
    }
  };

  if (success) {
    return (
      <>
        <IntakeHeader />
        <div className="intake-form-page bkg-alt flex js-c">
          <div className="intake-card">
            <h2 className="page-title">Registration Successful</h2>
            <p className="page-subtitle">
              Your account has been created and is pending approval. You will
              receive an email once your account is approved.
            </p>
            <Link
              to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
              className="button allan-koba flex js-c"
            >
              Proceed to Login
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <IntakeHeader />
      <div className="intake-form-page bkg-alt flex js-c">
        <div className="intake-card">
          <h2 className="page-title">
            <span>Create an Account for</span>
            {organizationSlug === "allan-koba" ? (
              <img
                src="/static/images/logo-black.png"
                alt={formatOrganizationSlug(organizationSlug) || "Organization"}
                className="logo-image"
                height={30}
              />
            ) : (
              <span className="organization-name">
                {formatOrganizationSlug(organizationSlug) || "Organization"}
              </span>
            )}
          </h2>
          <p className="page-subtitle">
            Complete the form below and you will be approved by an
            administrator.
          </p>

          <form onSubmit={handleFormSubmit(onSubmit)}>
            <ControlledInput
              control={control}
              name="name"
              label="Company Name"
              errors={{}}
              maxLength={40}
              onSave={handleFieldChange}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="email"
              label="Email Address"
              errors={{}}
              maxLength={50}
              onSave={handleFieldChange}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="phone_number"
              label="Phone Number"
              errors={{}}
              maxLength={10}
              onSave={handleFieldChange}
              componentType={TextInput}
            />

            <ControlledInput
              control={control}
              name="password"
              label="Password"
              errors={{}}
              maxLength={40}
              onSave={handleFieldChange}
              componentType={TextInput}
              type="password"
            />

            <ControlledInput
              control={control}
              name="password_confirm"
              label="Confirm Password"
              errors={{}}
              maxLength={40}
              onSave={handleFieldChange}
              componentType={TextInput}
              type="password"
            />

            <Button
              onClick={handleFormSubmit(onSubmit)}
              type="submit"
              className="button allan-koba mrg-top"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting || isLoading ? "Registering..." : "Register"}
            </Button>
          </form>

          <div className="prompt-message">
            Already have an account?{" "}
            <Link
              to={`/intake/${organizationSlug || "allan-koba"}/auth/login`}
              className="prompt-link"
            >
              Log in here
            </Link>
          </div>
        </div>
      </div>
      <KaboodleToaster.ToastContainer />
    </>
  );
};

export default RegisterIntake;
